// components/ImpersonationBanner.tsx
// Banner das angezeigt wird, wenn ein Admin als anderer User eingeloggt ist.
// Zeigt den Namen des impersonierten Users und einen "Zurück zum Admin" Button.
//
// Dieses Component sollte im Root-Layout eingebunden werden:
// z.B. in app/layout.tsx oder im PermissionsProvider

'use client'

import { useEffect, useState } from 'react'
import {
  isImpersonating,
  getImpersonatedUserId,
  stopImpersonation,
} from '@/lib/impersonation'
import { getSupabaseBrowserClient } from '@/lib/supabase'
import { Eye, ArrowLeft, X, Shield } from 'react-feather'

interface ImpersonatedUser {
  id: string
  name: string | null
  email: string | null
  role: string | null
  company_name: string | null
}

export default function ImpersonationBanner() {
  const [active, setActive] = useState(false)
  const [targetUser, setTargetUser] = useState<ImpersonatedUser | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!isImpersonating()) {
      setActive(false)
      return
    }

    setActive(true)
    const targetId = getImpersonatedUserId()
    if (!targetId) return

    // Lade die User-Daten des impersonierten Users
    const loadTargetUser = async () => {
      const supabase = getSupabaseBrowserClient()
      const { data } = await supabase
        .from('users')
        .select('id, name, email, role, company_name')
        .eq('id', targetId)
        .single()

      if (data) {
        setTargetUser(data)
      }
    }

    loadTargetUser()
  }, [])

  const handleStopImpersonation = async () => {
    setLoading(true)
    await stopImpersonation()
    // stopImpersonation() macht window.location.href redirect
  }

  if (!active) return null

  const displayName = targetUser?.name || targetUser?.email || 'Unbekannter User'
  const roleLabel = getRoleLabel(targetUser?.role)

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 99999,
        background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
        color: 'white',
        padding: '10px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        fontSize: '14px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
      }}
    >
      {/* Linke Seite: Info */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.2)',
            flexShrink: 0,
          }}
        >
          <Eye size={18} />
        </div>
        <div>
          <div style={{ fontWeight: 600, lineHeight: 1.3 }}>
            <Shield size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
            Admin-Impersonation aktiv
          </div>
          <div style={{ opacity: 0.9, fontSize: '13px' }}>
            Eingeloggt als: <strong>{displayName}</strong>
            {roleLabel && (
              <span
                style={{
                  marginLeft: '8px',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  background: 'rgba(255,255,255,0.2)',
                  fontSize: '11px',
                  fontWeight: 500,
                }}
              >
                {roleLabel}
              </span>
            )}
            {targetUser?.company_name && (
              <span style={{ marginLeft: '8px', opacity: 0.8 }}>
                ({targetUser.company_name})
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Rechte Seite: Zurück-Button */}
      <button
        onClick={handleStopImpersonation}
        disabled={loading}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 16px',
          background: 'white',
          color: '#dc2626',
          border: 'none',
          borderRadius: '6px',
          cursor: loading ? 'not-allowed' : 'pointer',
          fontSize: '13px',
          fontWeight: 600,
          opacity: loading ? 0.7 : 1,
          transition: 'all 0.2s',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
        onMouseEnter={(e) => {
          if (!loading) {
            e.currentTarget.style.background = '#fee2e2'
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'white'
        }}
      >
        <ArrowLeft size={14} />
        {loading ? 'Wird beendet...' : 'Zurück zum Admin'}
      </button>
    </div>
  )
}

// Rolle in lesbaren Text umwandeln
function getRoleLabel(role: string | null | undefined): string {
  if (!role) return ''
  const labels: Record<string, string> = {
    admin: 'Admin',
    subadmin: 'Sub-Admin',
    bestatter: 'Bestatter',
    user: 'Endkunde',
    sds_user: 'SDS User',
  }
  return labels[role] || role
}
